import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  Collapse,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  History,
  AccountTree,
} from '@mui/icons-material';
import apiService from '../../services/api';

interface Amendment {
  id: string;
  accessionId: string;
  version: string;
  amendedFromId: string;
  amendedAt: string;
  amendedBy: string;
  status: string;
  collectionDatetime: string;
  specimenClass: string;
  orderingPhysician: string;
  clinicalHistory: string;
}

interface Addendum {
  id: string;
  reason: string;
  partAffected: string;
  comment: string;
  createdAt: string;
  createdBy: string;
}

interface AmendmentHistoryProps {
  orderId?: string;
  accessionId?: string;
  mode: 'history' | 'chain'; // history = amendments from this order, chain = full lineage
  title?: string; // Optional custom title
  showAddendumHistory?: boolean; // If true, show addendum history instead of amendment chain
  addenda?: Addendum[]; // Addenda data when showing addendum history
}

const AmendmentHistory: React.FC<AmendmentHistoryProps> = ({ 
  orderId, 
  accessionId, 
  mode = 'chain',
  title,
  showAddendumHistory = false,
  addenda = []
}) => {
  const [amendments, setAmendments] = useState<Amendment[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (showAddendumHistory) {
      // When showing addendum history, use the provided addenda data
      setAmendments([]);
      setLoading(false);
    } else if ((mode === 'history' && orderId) || (mode === 'chain' && accessionId)) {
      fetchAmendments();
    }
  }, [orderId, accessionId, mode, showAddendumHistory, addenda]);

  const fetchAmendments = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let response;
      
      if (mode === 'history' && orderId) {
        response = await apiService.getAmendmentHistory(orderId);
      } else if (mode === 'chain' && accessionId) {
        response = await apiService.getAmendmentChain(accessionId);
      }
      
      if (response?.success) {
        setAmendments(response.data || []);
      } else {
        setError('Failed to fetch amendment data');
      }
    } catch (error) {
      console.error('Error fetching amendments:', error);
      setError('Failed to fetch amendment data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getVersionColor = (version: string) => {
    const majorVersion = parseInt(version.split('.')[0]);
    const colors = ['default', 'primary', 'secondary', 'success', 'warning', 'error'];
    return colors[majorVersion % colors.length] as any;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if ((showAddendumHistory && addenda.length === 0) || (!showAddendumHistory && amendments.length === 0)) {
    return (
      <Box p={2} textAlign="center">
        <Typography variant="body2" color="textSecondary">
          {showAddendumHistory ? 'No addendum history available' : 
           mode === 'history' ? 'No amendments from this order' : 'No amendment history available'}
        </Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={1} sx={{ mt: 2 }}>
      <Box p={2}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            {mode === 'history' ? <History fontSize="small" /> : <AccountTree fontSize="small" />}
            <Typography variant="h6">
              {title || 
               (showAddendumHistory ? 'Addendum History' :
                mode === 'history' ? 'Amendment History' : 'Amendment Chain')}
            </Typography>
            <Chip 
              label={showAddendumHistory ? addenda.length : amendments.length} 
              size="small" 
              color="primary" 
            />
          </Box>
          <IconButton onClick={() => setExpanded(!expanded)} size="small">
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Divider sx={{ my: 2 }} />
          
          {error && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          <List dense>
            {showAddendumHistory ? (
              // Display addenda
              addenda.map((addendum, index) => (
                <ListItem key={addendum.id} divider={index < addenda.length - 1}>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Chip 
                          label="Addendum"
                          size="small"
                          color="info"
                        />
                        <Typography variant="caption" color="textSecondary">
                          {formatDate(addendum.createdAt)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          <strong>Reason:</strong> {addendum.reason}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          <strong>Part Affected:</strong> {addendum.partAffected}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          <strong>Comment:</strong> {addendum.comment}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          <strong>Created by:</strong> {addendum.createdBy}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))
            ) : (
              // Display amendments
              amendments.map((amendment, index) => (
                <ListItem key={amendment.id} divider={index < amendments.length - 1}>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Chip 
                          label={`v${amendment.version}`}
                          size="small"
                          color={getVersionColor(amendment.version)}
                        />
                        <Chip 
                          label={amendment.status}
                          size="small"
                          variant="outlined"
                        />
                        <Typography variant="caption" color="textSecondary">
                          {formatDate(amendment.amendedAt)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          <strong>Amended by:</strong> {amendment.amendedBy}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          <strong>Specimen:</strong> {amendment.specimenClass}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          <strong>Physician:</strong> {amendment.orderingPhysician}
                        </Typography>
                        {amendment.clinicalHistory && (
                          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            <strong>Clinical History:</strong> {amendment.clinicalHistory}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))
            )}
          </List>

          <Box display="flex" justifyContent="space-between" mt={2}>
            <Button size="small" onClick={fetchAmendments}>
              Refresh
            </Button>
            <Typography variant="caption" color="textSecondary">
              {showAddendumHistory ? 'Showing addendum and amendment history' :
               mode === 'chain' ? 'Showing full amendment lineage' : 'Showing amendments from this order'}
            </Typography>
          </Box>
        </Collapse>
      </Box>
    </Paper>
  );
};

export default AmendmentHistory;